import { ChipOwnProps } from "@mui/material"
import currency from "currency.js"
import moment from "moment"

export const formatCPF = (value: string) => {
    return value.replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
}

export const formatCreditCard = (value: string) => {
    const cleanedValue = value.replace(/\D/g, ''); 

    if (cleanedValue.startsWith('37') || cleanedValue.startsWith('34')) {
        return cleanedValue
            .slice(0, 15)
            .replace(/(\d{4})(\d{6})(\d{5})/, '$1 $2 $3');
    } else {
        
        return cleanedValue
            .slice(0, 16)
            .replace(/(\d{4})(\d{4})(\d{4})(\d{4})/, '$1 $2 $3 $4');
    }
};

export const formatCVV = (value: string) => {
    return value.replace(/\D/g, '')
    .replace(/(\d{3})(\d)/, '$1')
}

export const formatExpiry = (value: string) => {
    return value.replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1/$2')
    .replace(/(\/\d{2})\d+?$/, '$1')
}

export const formatNumberCompact = (value: number) => {
    const formatter = Intl.NumberFormat('en-US', {notation: 'compact'})

    return formatter.format(value)
}

export const formatNumber = (value: number) => {

    return value.toLocaleString('pt-BR')
}

export const formatCNPJ = (value: string) => {
    return value.replace(/\D/g, '')
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,4})/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1')
    
    .replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5")
}

export const formatCEP = (value: string) => {
    return value.replace(/\D/g,'').replace(/(\d{5})(\d)/,'$1-$2')
}

export const getBidStatusColor = (status: string) => {
    let color: ChipOwnProps['color'];

    switch (status) {
        case 'PENDING':
            color = 'default'
            break
        case 'ACCEPTED':
            color = 'success'
            break
        case 'DENIED':
            color = 'error'
            break
        case 'AWAITING_ORDER':
            color = 'warning'
            break
        case 'DONE':
            color = 'info'
            break
        case 'ON_ORDER':
            color = 'warning'
            break
    }

    return color
}

export const getBidStatusLabel = (status: string) => {
    switch (status) {
        case 'PENDING':
            return 'Pendente'
        case 'ACCEPTED':
            return 'Aceita'
        case 'DENIED':
            return 'Recusada'
        case 'AWAITING_ORDER':
            return 'Aguardando Emissão'
        case 'ON_ORDER':
            return 'Aguardando Emissão Compatível'
        case 'DONE':
            return 'Finalizada'
    }
}

export const getOrderConfirmationColor = (row: {confirmation: string, created_at: Date}): ChipOwnProps['color'] => {
    if (row.confirmation === 'PENDING' && moment(row.created_at).isBefore(moment().subtract(48, 'hours'))) {
        return 'error'
    }

    switch (row.confirmation) {
        case 'PENDING':
            return 'info'
        case 'CANCELLED':
            return 'error'
        case 'CONFIRMED':
            return 'success'
        default:
            return 'default'
    }
}

export const getSupportTicketStatusLabel = (status: string) => {
    switch (status) {
        case 'OPEN':
            return 'Aberto'
        case 'CLOSED':
            return 'Fechado'
    }
}

export const getSupportTicketStatusColor = (status: string): ChipOwnProps['color'] => {
    switch (status) {
        case 'OPEN':
            return 'default'
        case 'CLOSED':
            return 'success'
    }
}

export const getOrderConfirmationLabel = (status: string) => {
    switch (status) {
        case 'PENDING':
            return 'Pendente'
        case 'CANCELLED':
            return 'Cancelada'
        case 'CONFIRMED':
            return 'Confirmada'
        default:
            return status || '—'
    }
}

export const getPaymentStatusColor = (status: string): ChipOwnProps['color'] => {
    switch (status) {
        case 'AWAITING_ORDER_CONFIRMATION':
            return 'default'
        case 'CANCELLED':
            return 'error'
        case 'PENDING':
            return 'default'
        case 'PAID':
            return 'success'
        case 'LATE':
            return 'warning'
        default:
            return 'default'
    }
}

export const getPaymentStatusLabel = (status: string) => {
    switch (status) {
        case 'AWAITING_ORDER_CONFIRMATION':
            return 'Aguardando Confirmação'
        case 'CANCELLED':
            return 'Cancelado'
        case 'PENDING':
            return 'Pendente'
        case 'PAID':
            return 'Pago'
        case 'LATE':
            return 'Atrasado'
        default:
            return status || '—'
    }
}

export const formatMoney = (value: number) => {
    return currency(value).format({
        symbol: 'R$ ',
        decimal: ',',
        separator: '.'
    })    
}

export const getPixTypeLabel = (value: string) => {
    switch (value) {
        case 'CPF':
            return 'CPF'
        case 'EMAIL':
            return 'E-mail'
        case 'PHONE_NUMBER':
            return 'Telefone'
        case 'CNPJ':
            return 'CNPJ'
        case 'RANDOM_KEY':
            return 'Chave Aleatória'
    }
}

const BR_TIMEZONE = 'America/Sao_Paulo'

const toBrCalendarDate = (d: Date): string => {
    // Use pt-BR business calendar (Horário de Brasília) to avoid UTC day shifting (e.g. after 21h).
    // en-CA yields YYYY-MM-DD with Intl.
    const dtf = new Intl.DateTimeFormat('en-CA', {
        timeZone: BR_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
    return dtf.format(d)
}

/** Calendar date as YYYY-MM-DD aligned to Horário de Brasília. */
export const toCalendarDateString = (value: string | Date | null | undefined): string => {
    if (!value) return ''

    if (typeof value === 'string') {
        const raw = value.trim()
        if (!raw) return ''
        // If already date-only, keep it as-is to avoid accidental timezone shifts.
        if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
        const d = new Date(raw)
        return Number.isNaN(d.getTime()) ? '' : toBrCalendarDate(d)
    }

    const d = value instanceof Date ? value : new Date(value)
    return Number.isNaN(d.getTime()) ? '' : toBrCalendarDate(d)
}

export const momentFromCalendarDate = (value: string | Date | null | undefined) => {
    const str = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? value
        : toCalendarDateString(value)
    if (!str) return null
    return moment(str, 'YYYY-MM-DD', true)
}

export const formatDate = (value: Date) => {
    const calendar = toCalendarDateString(value)
    return calendar ? moment(calendar, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''
}

export const formatDateTime = (value: Date) => {
    return moment(value).format('DD/MM/YYYY HH:mm:ss')
}

export const formatTime = (value: Date | string | null | undefined) => {
    if (!value) return ''
    const parsed = moment(value)
    return parsed.isValid() ? parsed.format('HH:mm') : ''
}

export const capitalizeFirstLetter = (label: string) => {
    return label.charAt(0).toUpperCase() + label.slice(1)
}

export const nl2br = (text: string) => text.replace(/\n/g, "<br />")

export const getSVGString = (svgNode: HTMLElement): string => {
    svgNode.setAttribute('xlink', 'http://www.w3.org/1999/xlink')
    
    const getCSSStyles = (parentElement: HTMLElement) => {
      let selectorTextArr = []
  
      selectorTextArr.push('#' + parentElement.id)
    }
  
    let cssStyleText = getCSSStyles(svgNode)
  
    let serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svgNode)
  
    return svgString
  }
  

export const randomHexColorCode = () => {
    let n = (Math.random() * 0xfffff * 1000000).toString(16);
    return '#' + n.slice(0, 6);
};

export const composeSummary = ({
   id,
   reservationCode,
   company,
   miles,
   fees,
   additional_cost,
   total 
}: {
    id: number,
    reservationCode: string,
   company: string,
   miles: number,
   fees: number,
   additional_cost?: number,
   total: number
}) => {
    const cleanTotal = currency(total).subtract(fees).subtract(additional_cost ?? 0).value
    const soldK = currency(cleanTotal, {precision: 3}).divide(currency(miles, {precision: 3}).divide(1000)).value
    const program = company.replace(/\s*\bliminar\b/gi, '').trim()

    return `Localizador: *${reservationCode}*\nPrograma: ${program}\n\n📊 Cálculo:\n🛫 Milhas: ${formatNumber(miles)}\n🛫 Milheiro: ${formatMoney(soldK)}\n💸 Taxa embarque: ${formatMoney(fees)}\n🧳 Bagagem/Outros Serviços: ${formatMoney(additional_cost || 0)}\n\n💰 *Total: ${formatMoney(total)}*\n\nNúmero da sorte BF: ${id}\n\n⚠️ _Gentileza confirmar com “ok” após conferir todos os dados do bilhete, até às 20h do dia da emissão. Na ausência de manifestação, será considerada como aceite._\n\n*PIX (CNPJ): 45.176.365/0001-00*`
}

export const shouldKeepCanceledCpfAsUsed = (company?: string | null): boolean => {
    const normalized = String(company || '').toUpperCase()
    return (
        normalized === 'LATAM' ||
        normalized === 'GOL' ||
        normalized === 'GOL_A_PRAZO' ||
        (
            normalized.includes('SMILES') &&
            normalized !== 'MILES_AND_SMILES'
        )
    )
}

type Emissao = {
    reservation_code: string
    created_at: string // ex: '2026-02-03'
    sell_price: number
  }


  export function composeIncomeSummary(
    emissoes: Emissao[],
    clientes: string[] | string
  ): string {
    if (!emissoes.length) {
      throw new Error('Lista de emissões vazia')
    }
  
    // Normaliza e remove duplicados
    const listaClientes = [
      ...new Set(Array.isArray(clientes) ? clientes : [clientes]) as unknown as string[]
    ].join(' + ')
  
    // Ordena por data
    const emissoesOrdenadas = [...emissoes].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )
  
    const dataInicio = moment(emissoesOrdenadas[0].created_at).format('DD/MM')
    const dataFim = moment(
      emissoesOrdenadas[emissoesOrdenadas.length - 1].created_at
    ).format('DD/MM')
  
    const periodo =
      dataInicio === dataFim ? dataInicio : `${dataInicio} a ${dataFim}`
  
    const totalEmissoes = emissoes.length
    const valorTotal = emissoes.reduce((acc, e) => acc + e.sell_price, 0)
  
    const listaLocalizadores = emissoesOrdenadas
      .map(e => `- ${e.reservation_code}\t${formatMoney(e.sell_price)}`)
      .join('\n')
  
    return `🌟 Fechamento de Emissões — Next Gate 🌟 ${listaClientes}
  📅 Período: ${periodo}
  📌 Detalhamento de emissões:
  
  🔹 *Localizadores:*
  ${listaLocalizadores}
  
  🧾 Resumo: 
  🔹 Emissões realizadas: ${totalEmissoes}
  🔹 Valor total a pagar: *${formatMoney(valorTotal)}*
  
  💳 Dados para pagamento via Pix:
  🔹 Chave PIX (CNPJ): 45.176.365/0001-00
  🔹 Banco Nubank`
  }

  export const formatAvailability = (data: any) => {
    if (!data || typeof data !== "object") {
      return ""
    }
  
    const daysMap: Record<string, string> = {
      monday: "Segunda",
      tuesday: "Terça",
      wednesday: "Quarta",
      thursday: "Quinta",
      friday: "Sexta",
      saturday: "Sábado",
      sunday: "Domingo",
    }
  
    const hoursMap: Record<string, string> = {
      morning: "Manhã",
      afternoon: "Tarde",
      evening: "Noite",
    }
  
    const selectedDays = Object.entries(daysMap)
      .filter(([key]) => data?.[key] === true)
      .map(([, label]) => label)
  
    const selectedHours = Object.entries(hoursMap)
      .filter(([key]) => data?.[key] === true)
      .map(([, label]) => label)
  
    const daysText = selectedDays.join(", ")
    const hoursText = selectedHours.join(", ")
  
    if (!daysText && !hoursText) return "Nenhuma disponibilidade"
    if (daysText && hoursText) return `${daysText} | ${hoursText}`
  
    return daysText || hoursText
  }